import "./RouteInfoPanel.css"

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faBookmark as farBookmark} from '@fortawesome/free-regular-svg-icons';
import {
    faBookmark,
    faFlag,
    faMountain,
    faBolt,
    faChevronUp,
    faChevronDown,
    faCheck,
    faGrip,
    faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import {useContext, useEffect, useState, useCallback, useRef} from "react";
import {UserActions, UserContext, UserDispatchContext} from "../../contexts/UserContext";
import {UserActivity, UserActivityType} from "../../model/User";
import SpinLoadingIndicator from "../../common/SpinLoadingIndicator";
import ErrorMessage from "../../common/ErrorMessage";
import {Route} from "../../model/Wall";
import {ScreenDimensions, useScreenDimensions} from "../../common/CustomHooks";
import RouteInfoList from "./RouteInfoList";
import RouteActivityButtons from "./RouteActivityButtons";
import RouteComments from "./RouteComments";
import Hammer from "hammerjs";
import {int} from "three/nodes";
import {NotificationActions, NotificationDispatchContext} from "../../contexts/NotificationContext";
import {UiActions, UiDispatchContext} from "../../contexts/UiContext";

export default function RouteInfoPanel({route, onClickBack}) {
    const user = useContext(UserContext);
    const userDispatch = useContext(UserDispatchContext);
    const notificationDispatch = useContext(NotificationDispatchContext);
    const uiStateDispatch = useContext(UiDispatchContext);

    const resetErrors = useCallback(() => {
        userDispatch({
            type: UserActions.DeleteErrors
        });
    }, [userDispatch]);

    function askToLogIn() {
        uiStateDispatch({type: UiActions.SetSidePanelPageIdx, pageIdx: 1});
    }

    function handleActivityAdd(activityType) {
        if (user.value === null) {
            askToLogIn();
            return;
        }
        const timestamp = new Date(Date.now());
        if (true) {
            // TODO: REMOVE
            // timestamp.setDate(Math.floor(Math.random() * 5));
        }
        userDispatch({
            type: UserActions.AddActivity,
            activity: new UserActivity({
                type: activityType,
                route: route,
                wallRoute: route,
                timestamp: timestamp,
            }),
        });
        resetErrors();
    }

    function handleActivityRemove(activityType) {
        if (user.value === null)
            return; // This shouldn't happen.
        userDispatch({
            type: UserActions.RemoveActivity,
            activity: {
                type: activityType,
                routeId: route.id,
            },
        });
        resetErrors();
    }

    function handleBookmarkClick() {
        if (isSavingActivity)
            return;
        (bookmarked ? handleActivityRemove : handleActivityAdd)(UserActivityType.Bookmark);
    }

    useEffect(() => {
        return () => resetErrors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const bookmarked = user.value == null ? false : user.value.isRouteBookmarked(route);
    const isSavingActivity = user.value == null ? false : user.status.isLoading(route.id);
    const error = user?.status.getError(route.id);
    const [screenWidth, screenHeight] = useScreenDimensions();
    if (screenWidth > ScreenDimensions.MEDIUM_WIDTH) {
        return <RouteInfoPanelDesktop
            route={route}
            user={user}
            isSavingActivity={isSavingActivity}
            handleActivityAdd={handleActivityAdd}
            handleActivityRemove={handleActivityRemove}
            handleBookmarkClick={handleBookmarkClick}
            bookmarked={bookmarked}
            error={error}
            onClickBack={onClickBack}
        />
    } else {
        return <RouteInfoPanelMobile
            route={route}
            user={user}
            isSavingActivity={isSavingActivity}
            handleActivityAdd={handleActivityAdd}
            handleActivityRemove={handleActivityRemove}
            handleBookmarkClick={handleBookmarkClick}
            bookmarked={bookmarked}
            error={error}
            onClickBack={onClickBack}
        />
    }
}

function RouteInfoPanelDesktop(
    {
        route,
        user,
        isSavingActivity,
        handleActivityAdd,
        handleActivityRemove,
        handleBookmarkClick,
        bookmarked,
        error,
        onClickBack,
    }) {
    return (
        <div id="info-panel">
            <FontAwesomeIcon
                icon={faArrowLeft}
                className="absolute left-0 top-0 p-5 cursor-pointer"
                size="lg"
                onClick={onClickBack}
            />
            <RouteInfoList route={route} onBookmarkClick={handleBookmarkClick} bookmarked={bookmarked}
                           isBookmarkDisabled={isSavingActivity}/>
            <div className="mb-6"></div>
            <RouteActivityButtons route={route} user={user} isDisabled={isSavingActivity}
                                  handleActivityAdd={handleActivityAdd}
                                  handleActivityRemove={handleActivityRemove}/>

            <div className="mb-3"></div>
            {isSavingActivity && <SpinLoadingIndicator/>}
            <ErrorMessage data={error}/>

            <div className="mb-8"></div>
            <RouteComments/>
        </div>
    );
}

function RouteInfoPanelMobile(
    {
        route,
        user,
        isSavingActivity,
        handleActivityAdd,
        handleActivityRemove,
        handleBookmarkClick,
        bookmarked,
        error,
        onClickBack,
    }) {
    const infoPanel = useRef();


    function getHeight() {
        return parseInt(window.getComputedStyle(infoPanel.current).height)
    }


    class PanelPlacements {
        static FullyShown = new PanelPlacements(() => (window.innerHeight - (10 + getHeight())));
        static PartiallyShown = new PanelPlacements(() => Math.floor(0.7 * window.innerHeight));
        static Hidden = new PanelPlacements(() => window.innerHeight - 30);

        constructor(topValueSupplier) {
            this.topValueSupplier = topValueSupplier;
        }

        topValue() {
            return this.topValueSupplier();
        }

        static getValues() {
            // TODO: remove PartiallyShown when the panel is too short
            return [this.FullyShown, this.PartiallyShown, this.Hidden];
        }

        static valueIndexFromTop(topValue) {
            const diffs = this.getValues().map(placement => Math.abs(placement.topValue() - topValue));
            return diffs.indexOf(Math.min(...diffs));
        }
    }

    const currentPlacementIdx = useRef(1);

    function animatePlacement(placementIdx) {
        const top = PanelPlacements.getValues()[placementIdx].topValue();
        infoPanel.current.style.transition = "top 0.3s ease-in-out";
        infoPanel.current.style.top = top + "px";
        currentPlacementIdx.current = placementIdx;
        // TODO: when fully visible, set bottom border to rounded
    }

    function setTop(targetValue) {
        infoPanel.current.style.transition = null;
        infoPanel.current.style.top = targetValue;
    }

    function onPanelClick() {
        const placementIdx = PanelPlacements.valueIndexFromTop(parseInt(window.getComputedStyle(infoPanel.current).top));
        if (placementIdx === 2) {
            animatePlacement(0);
        }
    }

    useEffect(() => {
        let topStart;
        let dragStartY;

        function onPanStart(event) {
            dragStartY = event.center.y;
            topStart = parseInt(window.getComputedStyle(infoPanel.current).top);
        }

        function onPan(event) {
            let newTop = topStart + event.center.y - dragStartY;

            const height = getHeight();
            let newBottom = window.innerHeight - (newTop + height);
            newBottom = Math.min(newBottom, 10);

            newTop = window.innerHeight - (newBottom + height);
            setTop(newTop + "px");
        }

        function onPanEnd(event) {
            const currentTop = parseInt(window.getComputedStyle(infoPanel.current).top);
            let newPlacementIdx = PanelPlacements.valueIndexFromTop(currentTop);
            if (newPlacementIdx === currentPlacementIdx.current) {
                if (event.deltaY > 40) {
                    newPlacementIdx = Math.min(newPlacementIdx + 1, PanelPlacements.getValues().length - 1);
                } else if (event.deltaY < -40) {
                    newPlacementIdx = Math.max(newPlacementIdx - 1, 0);
                }
            }
            animatePlacement(newPlacementIdx);
        }

        function snapToNearestPlacement() {
            const currentTop = parseInt(window.getComputedStyle(infoPanel.current).top);
            let placementIdx = PanelPlacements.valueIndexFromTop(currentTop);
            animatePlacement(placementIdx);
        }

        snapToNearestPlacement();

        // TODO: also enable mouse inputs
        const hammer = new Hammer(infoPanel.current);
        hammer.get("pan").set({direction: Hammer.DIRECTION_VERTICAL});
        hammer.on("panstart", onPanStart);
        hammer.on("pan", onPan);
        hammer.on("panend", onPanEnd);
        window.addEventListener("resize", snapToNearestPlacement);
        return () => {
            hammer.off("pan panstart panend");
            hammer.destroy();
            window.removeEventListener("resize", snapToNearestPlacement);
        }
    }, []);
    return (
        <div id="info-panel" ref={infoPanel} onClick={onPanelClick}>
            <FontAwesomeIcon
                icon={faArrowLeft}
                className="absolute left-0 top-0 pl-5 pr-3 pt-2 pb-0.5 cursor-pointer"
                size="lg"
                onClick={onClickBack}
            />
            <div className="flex justify-center items-center">
                <FontAwesomeIcon icon={faGrip}/>
            </div>
            <div className="mb-5"></div>
            <RouteActivityButtons route={route} user={user} isDisabled={isSavingActivity}
                                  handleActivityAdd={handleActivityAdd}
                                  handleActivityRemove={handleActivityRemove}/>
            <div className="mb-4"></div>
            <RouteInfoList route={route} onBookmarkClick={handleBookmarkClick} bookmarked={bookmarked}
                           isBookmarkDisabled={isSavingActivity}/>
            <div className="mb-3"></div>
            {isSavingActivity && <SpinLoadingIndicator/>}
            <ErrorMessage data={error}/>
        </div>
    );
}